import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { jobApplicantApi } from '../services/api';
import type {
  JobApplicantImportColumnMapping,
  JobApplicantImportValidateResponse,
  JobApplicantUpsertRequest,
} from '../types';

type Step = 'upload' | 'mapping' | 'preview' | 'done';

const mappingStorageKey = 'staffly.jobApplicantImport.mapping.v2';

const MAPPING_FIELDS: { key: keyof JobApplicantImportColumnMapping; label: string; required: boolean }[] = [
  { key: 'applicantName', label: 'Applicant name (legacy combined column)', required: false },
  { key: 'firstName', label: 'First name', required: false },
  { key: 'lastName', label: 'Last name', required: false },
  { key: 'applyingFor', label: 'Applying for', required: false },
  { key: 'sourceOfApplication', label: 'Source of application', required: false },
  { key: 'dateApplied', label: 'Date applied', required: false },
  { key: 'emailAddress', label: 'Email address', required: false },
  { key: 'phoneNumber', label: 'Phone number', required: false },
  { key: 'cvApplicationLink', label: 'Link to CV/Application form', required: false },
  { key: 'availabilityNoticePeriod', label: 'Availability / Notice Period', required: false },
  { key: 'initialScreenPassed', label: 'Initial screen passed?', required: false },
  { key: 'applicationScore', label: 'Application score', required: false },
  { key: 'invitedForInterview', label: 'Invited for interview?', required: false },
  { key: 'applicationStatus', label: 'Application status', required: false },
  { key: 'notes', label: 'Notes', required: false },
  { key: 'reasonForDecision', label: 'Reason for decision', required: false },
  { key: 'reference1Name', label: 'Reference 1 Name', required: false },
  { key: 'reference1ContactDetails', label: 'R1 Contact details', required: false },
];

type SheetData = {
  headers: string[];
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>;
};

const HEADER_HINTS = ['applicant name', 'applying for', 'source of application', 'date applied'];

function detectHeaderRowIndex(matrix: (string | number | undefined)[][]): number {
  const limit = Math.min(matrix.length, 15);
  for (let r = 0; r < limit; r++) {
    const rowText = matrix[r]
      .map((c) => String(c ?? '').trim().toLowerCase())
      .filter(Boolean)
      .join(' ');
    if (HEADER_HINTS.some((hint) => rowText.includes(hint))) return r;
  }
  return 0;
}

function parseMatrixToSheetData(matrix: (string | number | undefined)[][]): SheetData | null {
  if (!matrix.length) return null;
  const headerRowIndex = detectHeaderRowIndex(matrix);
  const rawHeaders = matrix[headerRowIndex].map((c) => String(c ?? '').trim());
  if (!rawHeaders.some(Boolean)) return null;

  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  let dataRowOrdinal = 0;
  for (let r = headerRowIndex + 1; r < matrix.length; r++) {
    const line = matrix[r];
    const cells: Record<string, string> = {};
    let any = false;
    rawHeaders.forEach((h, i) => {
      if (!h) return;
      const v = line[i];
      const cellStr = v === undefined || v === null ? '' : String(v).trim();
      cells[h] = cellStr;
      if (cellStr) any = true;
    });
    if (any) {
      dataRowOrdinal += 1;
      rows.push({ rowNumber: dataRowOrdinal, cells });
    }
  }

  return { headers: rawHeaders.filter(Boolean), rows };
}

function pickMappedColumn(stored: string | undefined | null, headers: string[]): string {
  if (stored == null) return '';
  const t = String(stored).trim();
  if (t === '') return '';
  const hit = headers.find((h) => h === t);
  return hit !== undefined ? hit : '';
}

function intersectMappingWithHeaders(
  m: Partial<JobApplicantImportColumnMapping>,
  headers: string[]
): JobApplicantImportColumnMapping {
  const result: JobApplicantImportColumnMapping = {};
  for (const field of MAPPING_FIELDS) {
    result[field.key] = pickMappedColumn(m[field.key], headers);
  }
  return result;
}

function autoSuggestMapping(headers: string[]): Partial<JobApplicantImportColumnMapping> {
  const lowerMap = new Map(headers.map((h) => [h.toLowerCase(), h]));
  const find = (...needles: string[]) => {
    for (const [lower, original] of Array.from(lowerMap.entries())) {
      if (needles.some((n) => lower.includes(n))) return original;
    }
    return '';
  };

  return {
    applicantName: find('applicant name', 'name/id', 'applicant'),
    firstName: find('first name', 'given name'),
    lastName: find('last name', 'surname', 'family name'),
    applyingFor: find('applying for', 'position', 'role'),
    sourceOfApplication: find('source of application', 'source'),
    dateApplied: find('date applied', 'applied'),
    emailAddress: find('email'),
    phoneNumber: find('phone'),
    cvApplicationLink: find('cv', 'application form', 'link to cv'),
    availabilityNoticePeriod: find('availability', 'notice period'),
    initialScreenPassed: find('initial screen'),
    applicationScore: find('application score', 'score'),
    invitedForInterview: find('invited for interview', 'interview'),
    applicationStatus: find('application status', 'status'),
    notes: find('notes'),
    reasonForDecision: find('reason for decision', 'decision'),
    reference1Name: find('reference 1 name', 'reference name'),
    reference1ContactDetails: find('r1 contact', 'reference 1 contact', 'reference contact'),
  };
}

function saveStoredMapping(m: JobApplicantImportColumnMapping) {
  try {
    localStorage.setItem(mappingStorageKey, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

function loadStoredMapping(): Partial<JobApplicantImportColumnMapping> {
  try {
    const raw = localStorage.getItem(mappingStorageKey);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<JobApplicantImportColumnMapping>;
  } catch {
    return {};
  }
}

export interface ImportJobApplicantsModalProps {
  open: boolean;
  onClose: () => void;
  onImportCommitted?: () => void;
}

export const ImportJobApplicantsModal: React.FC<ImportJobApplicantsModalProps> = ({
  open,
  onClose,
  onImportCommitted,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetsByName, setSheetsByName] = useState<Record<string, SheetData>>({});
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetRows, setSheetRows] = useState<Array<{ rowNumber: number; cells: Record<string, string> }>>([]);
  const [mapping, setMapping] = useState<JobApplicantImportColumnMapping>({});
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validateResult, setValidateResult] = useState<JobApplicantImportValidateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const headerOptions = useMemo(() => ['', ...headers], [headers]);

  useEffect(() => {
    if (!open) return;
    setMapping(intersectMappingWithHeaders(loadStoredMapping(), []));
    setStep('upload');
    setFileName('');
    setSheetNames([]);
    setSheetsByName({});
    setSelectedSheetName('');
    setHeaders([]);
    setSheetRows([]);
    setValidateResult(null);
    setError(null);
  }, [open]);

  const onSheetChange = useCallback(
    (sheetName: string) => {
      const data = sheetsByName[sheetName];
      if (!data) return;
      setSelectedSheetName(sheetName);
      setHeaders(data.headers);
      setSheetRows(data.rows);
      const stored = loadStoredMapping();
      const merged = { ...autoSuggestMapping(data.headers), ...stored };
      setMapping(intersectMappingWithHeaders(merged, data.headers));
    },
    [sheetsByName]
  );

  const parseFile = useCallback((file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const byName: Record<string, SheetData> = {};
        const names: string[] = [];

        for (const sheetName of wb.SheetNames) {
          const sheet = wb.Sheets[sheetName];
          const matrix = XLSX.utils.sheet_to_json<(string | number | undefined)[]>(sheet, {
            header: 1,
            defval: '',
            raw: false,
          });
          const parsed = parseMatrixToSheetData(matrix);
          if (parsed && parsed.headers.length > 0) {
            byName[sheetName] = parsed;
            names.push(sheetName);
          }
        }

        if (names.length === 0) {
          setError('No usable sheet found. Ensure the file has a header row with applicant columns.');
          return;
        }

        setFileName(file.name);
        setSheetNames(names);
        setSheetsByName(byName);
        onSheetChange(names[0]);
        setStep('mapping');
      } catch {
        setError('Could not read the file. Please upload a valid Excel (.xlsx) file.');
      }
    };
    reader.readAsArrayBuffer(file);
  }, [onSheetChange]);

  const hasNameMapping =
    Boolean(mapping.applicantName?.trim()) ||
    Boolean(mapping.firstName?.trim()) ||
    Boolean(mapping.lastName?.trim());

  const handleValidate = async () => {
    if (!hasNameMapping) {
      setError('Map at least one name column (combined applicant name, first name, or last name).');
      return;
    }
    setValidating(true);
    setError(null);
    try {
      const result = await jobApplicantApi.importValidate({
        fileName,
        mapping,
        rows: sheetRows,
      });
      setValidateResult(result);
      saveStoredMapping(mapping);
      setStep('preview');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Validation failed.';
      setError(msg);
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!validateResult) return;
    const applicants = validateResult.rows
      .filter((r) => r.status === 'Valid' && r.parsed)
      .map((r) => r.parsed as JobApplicantUpsertRequest);
    if (applicants.length === 0) {
      setError('No valid rows to import.');
      return;
    }
    setCommitting(true);
    setError(null);
    try {
      await jobApplicantApi.importCommit(applicants);
      setStep('done');
      onImportCommitted?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Import failed.';
      setError(msg);
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-semibold text-neutral-800">Import applicants from Excel</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'upload' && (
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 px-6 py-12 hover:border-blue-400">
              <Upload className="mb-3 h-10 w-10 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">Click to upload Excel file</span>
              <span className="mt-1 text-xs text-neutral-500">.xlsx or .xls — Staffly template supported</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) parseFile(file);
                  e.target.value = '';
                }}
              />
            </label>
          )}

          {step === 'mapping' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-neutral-600">File</label>
                  <div className="text-sm text-neutral-800">{fileName}</div>
                </div>
                {sheetNames.length > 1 && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600">Sheet</label>
                    <select
                      value={selectedSheetName}
                      onChange={(e) => onSheetChange(e.target.value)}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                    >
                      {sheetNames.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <p className="text-sm text-neutral-600">
                Map Excel columns to applicant fields ({sheetRows.length} data rows detected).
                Applicant IDs are generated automatically on import. Legacy spreadsheet columns like
                &quot;Applicant name/ID number&quot; can map to the combined name field — manual ID suffixes are ignored.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                {MAPPING_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label className="mb-1 block text-xs font-medium text-neutral-600">
                      {field.label}{field.required ? ' *' : ''}
                    </label>
                    <select
                      value={mapping[field.key] ?? ''}
                      onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                    >
                      {headerOptions.map((h) => (
                        <option key={h || '__none__'} value={h}>{h || '— Select column —'}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 'preview' && validateResult && (
            <>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-1 text-green-700">
                  <CheckCircle className="h-4 w-4" /> {validateResult.validCount} valid
                </span>
                <span className="inline-flex items-center gap-1 text-red-700">
                  <AlertCircle className="h-4 w-4" /> {validateResult.errorCount} errors
                </span>
              </div>
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Row</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validateResult.rows.map((row) => (
                      <tr key={row.rowNumber} className="border-t">
                        <td className="px-3 py-2">{row.rowNumber}</td>
                        <td className="px-3 py-2">
                          <span className={row.status === 'Valid' ? 'text-green-700' : 'text-red-700'}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {row.parsed
                            ? [row.parsed.firstName, row.parsed.lastName].filter(Boolean).join(' ')
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-neutral-600">{row.message ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center py-8 text-center">
              <CheckCircle className="mb-3 h-12 w-12 text-green-600" />
              <p className="text-lg font-medium text-neutral-800">Import complete</p>
              <p className="mt-1 text-sm text-neutral-600">
                {validateResult?.validCount ?? 0} applicant(s) were imported.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          {step === 'mapping' && (
            <>
              <button type="button" onClick={() => setStep('upload')} className="rounded-md px-4 py-2 text-sm hover:bg-neutral-100">
                Back
              </button>
              <button
                type="button"
                onClick={() => void handleValidate()}
                disabled={validating}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {validating && <Loader2 className="h-4 w-4 animate-spin" />}
                Preview import
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button type="button" onClick={() => setStep('mapping')} className="rounded-md px-4 py-2 text-sm hover:bg-neutral-100">
                Back
              </button>
              <button
                type="button"
                onClick={() => void handleCommit()}
                disabled={committing || !validateResult?.validCount}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {committing && <Loader2 className="h-4 w-4 animate-spin" />}
                Import {validateResult?.validCount ?? 0} record(s)
              </button>
            </>
          )}
          {(step === 'upload' || step === 'done') && (
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm hover:bg-neutral-100">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
